import * as React from "react";
import Card from "@mui/material/Card";
import OutlinedInput from "@mui/material/OutlinedInput";
import CardHeader from "@mui/material/CardHeader";
import Divider from "@mui/material/Divider";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Unstable_Grid2";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import CardActions from "@mui/material/CardActions";
import Button from "@mui/material/Button";
import type Framework from "benchmarking-domain/framework";
import {FrameworkStatus} from "benchmarking-domain/framework";
import {useEffect, useState} from "react";
import {Autocomplete, TextField} from "@mui/material";
import Chip from "@mui/material/Chip";

interface FrameworkFormProps {
  framework?: Framework;
  onSubmit: (data: FrameworkFormData) => Promise<void>;
}

export interface FrameworkFormData {
  name: string,
  technologies: string[],
  status: FrameworkStatus
}

export function FrameworkForm({framework, onSubmit}: FrameworkFormProps): React.JSX.Element {
  const [prevFramework, setPrevFramework] = useState<Framework | undefined>(framework);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const [name, setName] = useState<string>(framework?.name ?? "");
  const [technologies, setTechnologies] = useState<string[]>(framework?.technologies ?? []);
  const [status, setStatus] = useState<FrameworkStatus>(framework?.status ?? FrameworkStatus.Draft);

  if(prevFramework !== framework) {
    setPrevFramework(framework);
    setName(framework?.name ?? "");
    setTechnologies(framework?.technologies ?? []);
    setStatus(framework?.status ?? FrameworkStatus.Draft);
  }

  const saveData = async ():Promise<void> => {
    setIsSaving(true);

    try {
      await onSubmit({name, technologies, status});
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <form
      onSubmit={async (event) => {
        event.preventDefault();
        await saveData();
      }}
    >
      <Card>
        <CardHeader subheader="The information can be edited" title="Framework"/>
        <Divider/>
        <CardContent>
          <Grid container spacing={3}>
            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Name</InputLabel>
                <OutlinedInput onChange={(e) => {
                  setName(e.target.value);
                }} value={name} label="Name" name="name"/>
              </FormControl>
            </Grid>
            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <Autocomplete
                  multiple
                  id="technologies-filled"
                  options={[]}
                  freeSolo
                  autoSelect
                  value={technologies}
                  onChange={(e, value) => {
                    setTechnologies(value);
                  }}
                  renderTags={(value: string[], getTagProps) =>
                    value.map((option: string, index: number) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        {...getTagProps({index})}
                        key={option}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Technologies"
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid md={6} xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select value={status} onChange={(e) => {
                  setStatus(e.target.value as FrameworkStatus);
                }}
                        label="Status" name="status" variant="outlined">
                  {Object.values(FrameworkStatus).map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
        <Divider/>
        <CardActions sx={{justifyContent: 'flex-end'}}>
          <Button type="submit" variant="contained" disabled={isSaving}>Save details</Button>
        </CardActions>
      </Card>
    </form>
  );
}
